// ConfigureContentPack.jsx
import React, { useState, useEffect,useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Typography,
  IconButton,
  Alert
} from "@mui/material";
import {
  getContentPackReportPagesList,
  getHVAobjectFilters,
  getAllReportNotificationObjectFiltersList,
  updateReportNotificationObjectFilters,
  deleteReportNotificationObjectFilter,
  updatePropertiesReportNotification,
} from "../../services/reportNotificationService";
import CloseIcon from "@mui/icons-material/Close";
import InDataGridContainer from '../grid/InDataGridContainer';
import { useGridApiRef } from "@mui/x-data-grid-premium";

const ConfigureContentPack = ({
  open,
  onClose,
  subscription,
  tenantId,
  onSave,
}) => {
  const [availablePages, setAvailablePages] = useState([]);
  const [selectedPages, setSelectedPages] = useState([]);
  const [filters, setFilters] = useState([]);
  const [tables, setTables] = useState([]);
  const [columns, setColumns] = useState([]);
  const [selectedTable, setSelectedTable] = useState("");
  const [selectedColumn, setSelectedColumn] = useState("");
  const [filterValue, setFilterValue] = useState("");
  // NEW: Store the full HVA object filter list separately
  const [hvaObjectFilters, setHvaObjectFilters] = useState([]);
  // NEW: Store search terms for filtering
  const [availablePagesSearch, setAvailablePagesSearch] = useState("");
  const [selectedPagesSearch, setSelectedPagesSearch] = useState("");
  // Add these state variables at the top with other states
  const [isEditingFilter, setIsEditingFilter] = useState(false);
  const [editingFilterIndex, setEditingFilterIndex] = useState(-1);
  const [selectedAvailableItems, setSelectedAvailableItems] = useState([]);
  const [selectedSelectedItem, setSelectedSelectedItem] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");  
  const [showErrorDetail, setShowErrorDetail] = useState(false);  
  const [errorDetail, setErrorDetail] = useState("");
  const [duplicateError, setDuplicateError] = useState(false);
  const apiRef = useGridApiRef();
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);  
const [pendingDeleteIndex, setPendingDeleteIndex] = useState(-1);
const availablePagesSearchRef = useRef(null);
const firstTabPressed = useRef(false);
const availableItemsRefs = useRef([]);  
const selectedItemsRefs = useRef([]);
const [availableFocusedIndex, setAvailableFocusedIndex] = useState(-1); 
const [selectedFocusedIndex, setSelectedFocusedIndex] = useState(-1);

useEffect(() => {
  if (!open) return;

  const handleKeyDown = (e) => {
    if (e.key === "Tab" && !firstTabPressed.current) {
      e.preventDefault(); // prevent jumping to next element first
      availablePagesSearchRef.current?.focus();
      firstTabPressed.current = true;
    }
  };

  document.addEventListener("keydown", handleKeyDown);

  return () => {
    document.removeEventListener("keydown", handleKeyDown);
    firstTabPressed.current = false; // reset when dialog closes
  };
}, [open]);

useEffect(() => {  
  if (availableFocusedIndex >= 0) {  
    availableItemsRefs.current[availableFocusedIndex]?.focus();  
  }  
}, [availableFocusedIndex]);;

useEffect(() => {  
  if (selectedFocusedIndex >= 0) {  
    selectedItemsRefs.current[selectedFocusedIndex]?.focus();  
  }  
}, [selectedFocusedIndex]);
  useEffect(() => {
    if (open && subscription) {
        setSelectedAvailableItems([]);  
        setSelectedSelectedItem([]);  
        setSelectedPages([]);  
        setAvailablePagesSearch("");  
        setSelectedPagesSearch("");

        setSelectedTable("");  // Reset table name to default  
        setSelectedColumn(""); // Reset column name to default  
        setFilterValue("");    // Also reset filter value  
        setIsEditingFilter(false); // Reset editing state  
        setEditingFilterIndex(-1);  
        setErrorMessage("");  
        setErrorDetail("");  
        setShowErrorDetail(false); 
      loadContentPackPages();
      loadFilters();
    }
  }, [open, subscription]);

  // effect to check for duplicates  
useEffect(() => {  
  const hasDuplicate = selectedTable && selectedColumn &&   
    filters.some(f =>   
      f.TableName === selectedTable &&   
      f.ColumnName === selectedColumn  
    );  
  setDuplicateError(hasDuplicate && !isEditingFilter);  
}, [selectedTable, selectedColumn, filters, isEditingFilter]);

const filterColumns = [  
  { field: 'TableName', headerName: 'Table Name', flex: 1, minWidth: 120, editable: false },  
  { field: 'ColumnName', headerName: 'Column Name', flex: 1, minWidth: 120, editable: false },  
  { field: 'Value', headerName: 'Value', flex: 1, minWidth: 150, editable: false },  
  {   
    field: 'actions',   
    headerName: 'Actions',   
    displayName: 'Filter',  
    actions: 'edit, delete',   
    width: 120,   
    editable: false   
  }  
];

  const loadContentPackPages = async () => {
    try {
      const response = await getContentPackReportPagesList(
        subscription.ObjectId,
        tenantId
      );
      const pages = response.data || [];
      
      // Get selected pages first
      let selectedPagesList = [];
      if (subscription.Properties) {
        const properties = JSON.parse(subscription.Properties);
        if (properties.selectedContentPackPages) {
          selectedPagesList = properties.selectedContentPackPages;
          setSelectedPages(selectedPagesList);
        }
      }
       // Always set selectedPages, even if empty  
      setSelectedPages(selectedPagesList);  
      // Filter out selected pages from available pages
      const selectedPageNames = new Set(selectedPagesList.map((p) => p.name));
      const availablePagesList = pages.filter(
        (p) => !selectedPageNames.has(p.name)
      );
      setAvailablePages(availablePagesList);
      setErrorMessage("");
    } catch (error) {
      console.error("Error loading pages:", error);
      // Clear all form state when error occurs  
      setAvailablePages([]);  
      setSelectedPages([]);  
      setFilters([]);  
      setTables([]);  
      setColumns([]);  
      setSelectedTable("");  
      setSelectedColumn("");  
      setFilterValue("");  
      setHvaObjectFilters([]);  
      setSelectedAvailableItems([]);  
      setSelectedSelectedItem([]);  
       setErrorMessage("Getting content pack report pages failed.");
    }
  };

  const loadFilters = async () => {
    try {
      // Get all available HVA object filters for this object
      const tableResponse = await getHVAobjectFilters(
        subscription.ObjectId,
        true
      );
      const filterList = tableResponse.data || [];
      setHvaObjectFilters(filterList);

      // Extract unique table names
      const uniqueTables = [...new Set(filterList.map((f) => f.TableName))];
      setTables(uniqueTables.map((t) => ({ TableName: t })));

      // Get existing saved filters for this subscription
      if (subscription.SubscriptionID) {
        const existingFilters = await getAllReportNotificationObjectFiltersList(
          subscription.SubscriptionID
        );
        const savedFilters = existingFilters.data || [];

        // Join saved filters with HVA object filters to get table/column names
        const joinedFilters = savedFilters.map((savedFilter) => {
          const hvaFilter = filterList.find(
            (f) => f.HVAObjectFilterId === savedFilter.HVAObjectFilterId
          );
          return {
            ...savedFilter,
            id: savedFilter.HVAObjectFilterId,
            TableName: hvaFilter?.TableName || "",
            ColumnName: hvaFilter?.ColumnName || "",
            HVAObjectFilterId: savedFilter.HVAObjectFilterId,
          };
        });

        setFilters(joinedFilters);
      }
    } catch (error) {
      console.error("Error loading filters:", error);
      // Clear all form state when error occurs  
      setAvailablePages([]);  
      setSelectedPages([]);  
      setFilters([]);  
      setTables([]);  
      setColumns([]);  
      setSelectedTable("");  
      setSelectedColumn("");  
      setFilterValue("");  
      setHvaObjectFilters([]);  
      setSelectedAvailableItems([]);  
      setSelectedSelectedItem([]); 
    }
  };

  const handleSave = async () => {
    if (errorMessage) return;
    if (duplicateError) {
      return;
    }
    try {
      const updatedSubscription = {
        ...subscription,
        Properties: JSON.stringify({
          selectedContentPackPages: selectedPages,
        }),
      };

      await updatePropertiesReportNotification({
        reportNotification: updatedSubscription,
      });

      if (filters.length > 0) {
        // Prepare filters for API - only send the required fields
        const filtersToSave = filters.map((f) => ({
          HVAObjectFilterId: f.HVAObjectFilterId,
          SubscriptionID: subscription.SubscriptionID,
          Value: f.Value,
          Properties: "",
        }));
        await updateReportNotificationObjectFilters(filtersToSave);
      }

      onSave(updatedSubscription, filters);
      onClose();
    } catch (error) {
      console.error("Error saving configuration:", error);
    }
  };

  // NEW: Filter pages based on search
  const filteredAvailablePages = availablePages.filter((page) =>
    page.displayName.toLowerCase().includes(availablePagesSearch.toLowerCase())
  );

  const filteredSelectedPages = selectedPages.filter((page) =>
    page.displayName.toLowerCase().includes(selectedPagesSearch.toLowerCase())
  );

  const handleSelectPage = () => {
    if (selectedAvailableItems.length === 0) return;

    const pagesToAdd = selectedAvailableItems
      .map((name) => availablePages.find((p) => p.name === name))
      .filter(Boolean);

    if (pagesToAdd.length > 0) {
      setSelectedPages([...selectedPages, ...pagesToAdd]);
      const pageNamesToAdd = new Set(pagesToAdd.map((p) => p.name));
      setAvailablePages(
        availablePages.filter((p) => !pageNamesToAdd.has(p.name))
      );
      setSelectedAvailableItems([]); // Clear selection after moving
    }
  };

  const handleDeselectPage = () => {
    if (selectedSelectedItem.length === 0) return;

    const pagesToRemove = selectedSelectedItem
      .map((name) => selectedPages.find((p) => p.name === name))
      .filter(Boolean);

    if (pagesToRemove.length > 0) {
      setAvailablePages([...availablePages, ...pagesToRemove]);
      const pageNamesToRemove = new Set(pagesToRemove.map((p) => p.name));
      setSelectedPages(
        selectedPages.filter((p) => !pageNamesToRemove.has(p.name))
      );
      setSelectedSelectedItem([]); // Clear selection after moving
    }
  };

  // Use hvaObjectFilters to get columns for selected table
  const handleTableChange = (event) => {
    const tableName = event.target.value;
    setSelectedTable(tableName);

    //  Use hvaObjectFilters instead of filters to get available columns
    const tableFilters = hvaObjectFilters.filter(
      (f) => f.TableName === tableName
    );
    const uniqueColumns = [...new Set(tableFilters.map((f) => f.ColumnName))];
    setColumns(uniqueColumns.map((c) => ({ ColumnName: c })));
    setSelectedColumn("");
  };

  const handleAddFilter = () => {
    if (selectedTable && selectedColumn && filterValue) {
      const hvaFilter = hvaObjectFilters.find(
        (f) => f.TableName === selectedTable && f.ColumnName === selectedColumn
      );

      if (hvaFilter) {
        const newFilter = {
           id: hvaFilter.HVAObjectFilterId,
          TableName: selectedTable,
          ColumnName: selectedColumn,
          Value: filterValue,
          HVAObjectFilterId: hvaFilter.HVAObjectFilterId,
          IsNew: true,
        };

        if (isEditingFilter && editingFilterIndex >= 0) {
          const updatedFilters = [...filters];
          updatedFilters[editingFilterIndex] = newFilter;
          setFilters(updatedFilters);
          resetFilterForm();
        } else {
          setFilters([...filters, newFilter]);
          // Clear form after adding new filter  
          setSelectedTable("");  
          setSelectedColumn("");  
          setFilterValue("");  
          setColumns([]); 
        }
      }
    }
  };

  const confirmDeleteFilter = async (index) => {
    const filterToDelete = filters[index];
    setPendingDeleteIndex(index);  
    setShowDeleteAlert(true); 
  };

const performDeleteFilter = async () => {
  const filterToDelete = filters[pendingDeleteIndex];

  try {
    // Delete from server only if it has an ID
    if (filterToDelete?.HVAObjectFilterId) {
      await deleteReportNotificationObjectFilter(
        filterToDelete.HVAObjectFilterId,
        subscription.SubscriptionID
      );
    }

    // Remove from UI list
    setFilters(prev => prev.filter((_, i) => i !== pendingDeleteIndex));
  } catch (error) {
    console.error("Error deleting filter:", error);
  }

  // Always close alert
  setShowDeleteAlert(false);
};

  const handleEditFilter = (index) => {
    const filter = filters[index];
    setSelectedTable(filter.TableName);

    const tableFilters = hvaObjectFilters.filter(
      (f) => f.TableName === filter.TableName
    );
    const uniqueColumns = [...new Set(tableFilters.map((f) => f.ColumnName))];
    setColumns(uniqueColumns.map((c) => ({ ColumnName: c })));

    setSelectedColumn(filter.ColumnName);
    setFilterValue(filter.Value);
    setIsEditingFilter(true);
    setEditingFilterIndex(index);
  };

  const resetFilterForm = () => {
    setSelectedTable("");
    setSelectedColumn("");
    setFilterValue("");
    setIsEditingFilter(false);
    setEditingFilterIndex(-1);
    setColumns([]);
  };

const handleClose = () => { 
  // Clear all selection states  
  setSelectedAvailableItems([]);  
  setSelectedSelectedItem([]);  
  setSelectedPages([]);  
  setAvailablePagesSearch("");  
  setSelectedPagesSearch("");

  setSelectedTable("");  // Reset table name to default  
  setSelectedColumn(""); // Reset column name to default  
  setFilterValue("");    // Also reset filter value  
  setIsEditingFilter(false); // Reset editing state  
  setEditingFilterIndex(-1);  
  setErrorMessage("");  
  setErrorDetail("");  
  setShowErrorDetail(false);  
  onClose();  
};
return (  

    <Dialog  open={open}  onClose={(event, reason) => {  
      // Prevent closing on backdrop click  
      if (reason === 'backdropClick') {  
        return;  
      }  
      onClose();  
      }}  maxWidth="md"  fullWidth  PaperProps={{  sx: {  p: 1}}}  
    >  
      <DialogTitle  
        sx={{  
          pr: 1,  
          pl: 1,  
          pt: 2,  
          pb: 1,  
          fontWeight: 700,  
          fontSize: "1.25rem",  
          display: "flex",  
          alignItems: "center",  
          justifyContent: "space-between",  
        }}  
      >  
        Configure Content Pack Pages and Filters  
        <IconButton  
          aria-label="close"  
          onClick={handleClose}  
          sx={{ position: "absolute", right: 18, top: 18 }}  
        >  
          <CloseIcon />  
        </IconButton>  
      </DialogTitle>  

      <DialogContent dividers sx={{ p: 2 }}>  
         
        {/*  {errorMessage && (  
          <Alert  severity="error"  
            sx={{   
              mb: 2,  
              '& .MuiAlert-message': {   
                display: 'flex',   
                alignItems: 'center',   
                flex: 1   
              }  
            }}  >  
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>  
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>  
                {errorMessage}  
              </Typography>  
            </Box>  
              
            {showErrorDetail && errorDetail && (  
              <Box sx={{   
                mt: 2,   
                p: 1.5,   
                bgcolor: 'grey.50',   
                borderRadius: 1,  
                fontFamily: 'monospace',  
                fontSize: '0.75rem',  
                border: '1px solid',  
                borderColor: 'error.light'  
              }}>  
                <Typography variant="caption" sx={{ fontWeight: 'bold' }}>  
                  Error Details:  
                </Typography>  
                <pre style={{ margin: '8px 0 0 0', whiteSpace: 'pre-wrap' }}>  
                  {errorDetail}  
                </pre>  
              </Box>  
            )}  
          </Alert>  
          )} */}

        {/* Pages Selection */} 
        <Box sx={{ mb: 2 }}>  
          <Box  
            sx={{  
              display: "flex",  
              gap: 2,  
              alignItems: "flex-start",  
              border: "1px solid #e0e0e0",  
              borderRadius: 1,  
              p: 2,  
            }}  
          >  
            {/* Available Pages */}  
            <Box sx={{ flex: 1 }}>  
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>  
                Available Pages  
              </Typography>  
              <TextField  
                fullWidth  
                size="small"  
                placeholder="Search..."  
                value={availablePagesSearch}  
                onChange={(e) => setAvailablePagesSearch(e.target.value)}  
                sx={{ mb: 1 }}  
                inputRef={availablePagesSearchRef}
              />  
              <Box  
                tabIndex={0}
                sx={{  
                  height: 200,  
                  border: "1px solid #e0e0e0",  
                  borderRadius: 1,  
                  overflow: "auto",  
                  backgroundColor: "#fff",  
                  p: 1,  
                }}  
                 onKeyDown={(e) => {  
                  // Focus first item when arrow keys are pressed on container  
                  if ((e.key === 'ArrowDown' ) && filteredAvailablePages.length > 0) {  
                    // e.stopPropagation(); 
                    e.preventDefault();  
                    const firstItem = e.currentTarget.children[0];  
                    if (firstItem) firstItem.focus();  
                  }  
                }} 
 
              >  
                {filteredAvailablePages.length === 0 ? (  
                  <Typography sx={{ p: 2, color: "text.secondary" }}>  
                    No pages available  
                  </Typography>  
                ) : (  
                  filteredAvailablePages.map((page,index) => (  
                    <Box  
                      key={page.name}  
                      tabIndex={-1}
                      ref={el => availableItemsRefs.current[index] = el} 
                      sx={{ p: 1, cursor: "pointer", "&:hover": { backgroundColor: "#f5f5f5" }, borderBottom: "1px solid #f0f0f0", backgroundColor: selectedAvailableItems.includes( page.name ) ? "#e3f2fd" : "transparent","&:focus": {  // Add focus styles  
                        outline: "2px solid #1976d2",  
                        outlineOffset: "-2px"  
                      }  }}
                      
                      onClick={() => {  
                        if (selectedAvailableItems.includes(page.name)) {  
                          setSelectedAvailableItems(  
                            selectedAvailableItems.filter(  
                              (name) => name !== page.name  
                            )  
                          );  
                        } else {  
                          setSelectedAvailableItems([  
                            ...selectedAvailableItems,  
                            page.name,  
                          ]);  
                        }  
                      }}  

                      onKeyDown={(e) => {  
                        e.stopPropagation();  
                        
                        if (e.key === "ArrowDown") {  
                          e.preventDefault();  
                          const nextIndex = Math.min(index + 1, filteredAvailablePages.length - 1);  
                          setAvailableFocusedIndex(nextIndex);  
                        } else if (e.key === "ArrowUp") {  
                          e.preventDefault();  
                          const prevIndex = Math.max(index - 1, 0);  
                          setAvailableFocusedIndex(prevIndex);  
                        } else if (e.key === "Enter" || e.key === " ") {  
                          e.preventDefault();  
                          e.currentTarget.click();  
                        }  
                      }}
                    >  
                      <Typography variant="body2">  
                        {page.displayName} 
                      </Typography>  
                    </Box>  
                  ))  
                )}  
              </Box>  
            </Box>  
  
            {/* Move Buttons */}  
            <Box  
              sx={{  
                display: "flex",  
                flexDirection: "column",  
                gap: 1,  
                alignSelf: "center",
                alignItems: "center",  
              }}  
            >  
              <Button  
                variant="outlined"  
                onClick={handleSelectPage}  
                sx={{ minWidth: 60 }}  
                disabled={selectedAvailableItems.length === 0}  
              >  
                &gt;&gt;  
              </Button>  
              <Button  
                variant="outlined"  
                onClick={handleDeselectPage}  
                sx={{ minWidth: 60 }}  
                disabled={selectedSelectedItem.length === 0}  
              >  
                &lt;&lt;  
              </Button>  
            </Box>  
  
            {/* Selected Pages */}  
            <Box sx={{ flex: 1 }}>  
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>  
                Selected Pages  
              </Typography>  
              <TextField  
                fullWidth  
                size="small"  
                placeholder="Search..."  
                value={selectedPagesSearch}  
                onChange={(e) => setSelectedPagesSearch(e.target.value)}  
                sx={{ mb: 1 }}  
              />  
              <Box  
              tabIndex={0}
                sx={{  
                  height: 200,  
                  border: "1px solid #e0e0e0",  
                  borderRadius: 1,  
                  overflow: "auto",  
                  backgroundColor: "#fff",  
                  p: 1,  
                }}  
                onKeyDown={(e) => {  
                  // Focus first item when arrow keys are pressed on container  
                  if ((e.key === 'ArrowDown') && filteredSelectedPages.length > 0) {  
                    e.preventDefault();  
                    const firstItem = e.currentTarget.children[0];  
                    if (firstItem) firstItem.focus();  
                  }  
                }} 
              >  
                {filteredSelectedPages.length === 0 ? (  
                  <Typography sx={{ p: 2, color: "text.secondary" }}>  
                    No pages selected  
                  </Typography>  
                ) : (  
                  filteredSelectedPages.map((page,index) => (  
                    <Box  
                      key={page.name}  
                      tabIndex={-1} 
                      ref={el => selectedItemsRefs.current[index] = el}  
                     sx={{   
                        p: 1,   
                        cursor: "pointer",   
                        "&:hover": { backgroundColor: "#f5f5f5" },   
                        borderBottom: "1px solid #f0f0f0",   
                        backgroundColor: selectedSelectedItem.includes(page.name) ? "#e3f2fd" : "transparent",  
                        "&:focus": {  // Add focus styles  
                          outline: "2px solid #1976d2",  
                          outlineOffset: "-2px"  
                        }  
                      }} 
                      onClick={() => {  
                        if (selectedSelectedItem.includes(page.name)) {  
                          setSelectedSelectedItem(  
                            selectedSelectedItem.filter(  
                              (name) => name !== page.name  
                            )  
                          );  
                        } else {  
                          setSelectedSelectedItem([  
                            ...selectedSelectedItem,  
                            page.name,  
                          ]);  
                        }  
                      }} 
                      onKeyDown={(e) => {  
                        e.stopPropagation(); // prevent parent handling again  
                    
                        if (e.key === "ArrowDown") {  
                          e.preventDefault();  
                          const nextIndex = Math.min(index + 1, filteredSelectedPages.length - 1);  
                          setSelectedFocusedIndex(nextIndex);  
                        } else if (e.key === "ArrowUp") {  
                          e.preventDefault();  
                          const prevIndex = Math.max(index - 1, 0);  
                          setSelectedFocusedIndex(prevIndex);  
                        } else if (e.key === "Enter" || e.key === " ") {  
                          e.preventDefault();  
                          e.currentTarget.click();  
                        }  
                      }}   
                    >  
                      <Typography variant="body2">  
                        {page.displayName}  
                      </Typography>  
                    </Box>  
                  ))  
                )}  
              </Box>  
            </Box>  
          </Box>  
        </Box> 
  
        {/* Filters Configuration */}  
        <Box  
          sx={{  
            border: "1px solid #e0e0e0",  
            borderRadius: 1,  
            p: 2,  
          }} 
        >  
 
  
          {/* Filter Form */}  
          <Box sx={{   
            border: "1px solid #e0e0e0",   
            borderRadius: 2,   
            p: 3,   
            mb: 3,  
            backgroundColor: "#ffffffff"  
          }}>  
            
              
            <Box sx={{ display: "flex", gap: 3, mb: 3 }}>  
              <Box sx={{ flex: 1 }}>  
                <Typography className="lableFont" sx={{ mb: 1, fontWeight: 600 }}>  
                  Table Name  
                </Typography>  
                <FormControl fullWidth size="small">  
                  <InputLabel>Table Name</InputLabel>  
                  <Select  
                    value={selectedTable}  
                    onChange={handleTableChange}  
                    label="Table Name"  
                    disabled={isEditingFilter}  
                  >  
                    <MenuItem value="">Select Table Name</MenuItem>  
                    {tables.map((table) => (  
                      <MenuItem key={table.TableName} value={table.TableName}>  
                        {table.TableName}  
                      </MenuItem>  
                    ))}  
                  </Select>  
                </FormControl>  
              </Box>  
            
              <Box sx={{ flex: 1 }}>  
                <Typography className="lableFont" sx={{ mb: 1, fontWeight: 600 }}>  
                  Column Name  
                </Typography>  
                <FormControl fullWidth size="small">  
                  <InputLabel>Column Name</InputLabel>  
                  <Select  
                    value={selectedColumn}  
                    onChange={(e) => setSelectedColumn(e.target.value)}  
                    label="Column Name"  
                    disabled={!selectedTable || isEditingFilter}  
                  >  
                    <MenuItem value="">Select Column Name</MenuItem>  
                    {columns.map((column) => (  
                      <MenuItem key={column.ColumnName} value={column.ColumnName}>  
                        {column.ColumnName}  
                      </MenuItem>  
                    ))}  
                  </Select>  
                </FormControl>  
              </Box>  
            </Box>  
            
            <Box sx={{ display: "flex", gap: 3, alignItems: "flex-end" }}>    
              <Box sx={{ flex: 1 }}>    
                <Typography className="lableFont" sx={{ mb: 1, fontWeight: 600 }}>    
                  Value   
                </Typography>    
                <TextField    
                  sx={{ width: '57.5%' }}
                  size="small"    
                  label="Value"    
                  value={filterValue}    
                  onChange={(e) => setFilterValue(e.target.value)}    
                  placeholder="Enter filter value"    
                />    
              </Box>    
                  
              <Box sx={{ display: "flex", gap: 2 }}>  
                <Button    
                  variant="contained"    
                  onClick={handleAddFilter}    
                  disabled={!selectedTable || !selectedColumn || !filterValue ||    
                    (!isEditingFilter && filters.some(f =>     
                      f.TableName === selectedTable &&     
                      f.ColumnName === selectedColumn    
                    ))    
                  }    
                  sx={{ minWidth: 100, height: 40 }}    
                >    
                  {isEditingFilter ? "Update" : "Add"}    
                </Button>    
                    
                {isEditingFilter && (    
                  <Button    
                    variant="outlined"    
                    onClick={resetFilterForm}    
                    sx={{ minWidth: 100, height: 40 }}    
                  >    
                    Cancel    
                  </Button>    
                )}    
              </Box>    
            </Box>
          </Box>

            {/* Error message between sections */}  
           {duplicateError && !isEditingFilter && (    
            <Alert   
              severity="error"   
              sx={{ mb: 2 }}  
              action={  
                <IconButton  
                  aria-label="close"  
                  color="inherit"  
                  size="small"  
                  onClick={() => {  
                    setDuplicateError(false);  
                    setSelectedTable("");  
                    setSelectedColumn("");  
                  }}   
                >  
                  <CloseIcon fontSize="small" />  
                </IconButton>  
              }  
            >    
              An entry with this table and column already exists    
            </Alert>    
          )}   

          {/* Filters DataGrid */}  
          <InDataGridContainer
            autoHeight    
            apiRef={apiRef} 
            rows={filters}  
            columns={filterColumns}  
            getRowId={(row) => row.HVAObjectFilterId || `${row.TableName}-${row.ColumnName}`}  
            editable={false}  
            onDelete={(row) => {  
              const index = filters.findIndex(f =>   
                f.TableName === row.TableName &&   
                f.ColumnName === row.ColumnName  
              );  
              if (index !== -1) {  
                confirmDeleteFilter(index);  
              }  
            }}  
            onRowEdit={(id, row) => {  
              const index = filters.findIndex(f =>   
                f.TableName === row.TableName &&   
                f.ColumnName === row.ColumnName  
              );  
              if (index !== -1) {  
                handleEditFilter(index);  
              }  
            }}  
            showEditButton={() => true}  
            showDeleteButton={() => true}  
            pageSizeOptions={[5, 10, 25]}  
            sx={{ height: 300 }}  
          />  
        </Box>
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'flex-end', pr: 3, pb: 1 }}>
        <Button variant="contained" onClick={handleSave}>
          Save
        </Button>
        <Button variant="outlined" onClick={handleClose}>
          Cancel
        </Button>
      </DialogActions>
      {showDeleteAlert&&<Alert
        severity="warning"
        sx={{
          position: 'fixed',  
          left: '50%', 
          transform: 'translate(-50%,-50%)',
          zIndex: 9999,  
          minWidth: 300  
        }}  
        action={  
          <>  
            <Button   
              size="small"   
              onClick={async () => {performDeleteFilter()}}  
            >  
              OK  
            </Button>  
            <Button   
              size="small"   
              onClick={() => setShowDeleteAlert(false)}  
            >  
              Cancel  
            </Button>  
          </>  
        }  
      >  
        Are you sure you want to delete this filter?  
      </Alert>}
    </Dialog>  

  );  
};

export default ConfigureContentPack;
